import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, GripVertical, AlertTriangle } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import useToastStore from '../../store/toastStore';
import useBoardCan from '../../hooks/useBoardCan';
import { dialog } from '../../store/dialogStore';

// Mesma ordem/valores de backend/src/config/statusPhaseMap.js (PHASE_ORDER) —
// duplicado aqui de propósito: é uma constante de apresentação (rótulo em
// português + frase explicativa de uma linha), não vale importar código de
// servidor pro bundle do frontend por isto.
const PHASE_OPTIONS = [
  { value: '', label: 'Sem fase — não entra nas métricas de status' },
  { value: 'unstarted', label: 'Não iniciado — o trabalho ainda não começou' },
  { value: 'started', label: 'Em andamento — alguém está trabalhando agora' },
  { value: 'waiting', label: 'Esperando — parado aguardando outra pessoa ou etapa' },
  { value: 'completed', label: 'Concluído — o trabalho terminou' },
  { value: 'canceled', label: 'Cancelado — não vai mais ser feito' },
];

function hslToRgb(h, s, l) {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return { h: 0, s: 0, l: 0.5 };
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h = 0; const l = (mx + mn) / 2;
  const s = mx === mn ? 0 : l > 0.5 ? (mx - mn) / (2 - mx - mn) : (mx - mn) / (mx + mn);
  if (mx !== mn) {
    if (mx === r) h = (g - b) / (mx - mn) + (g < b ? 6 : 0);
    else if (mx === g) h = (b - r) / (mx - mn) + 2;
    else h = (r - g) / (mx - mn) + 4;
    h /= 6;
  }
  return { h, s, l };
}

function ColorWheel({ value, onPick, size = 180 }) {
  const canvasRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const { h, s } = hexToHsl(value);
  const [light, setLight] = useState(hexToHsl(value).l || 0.5);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const radius = size / 2;
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x - radius;
        const dy = y - radius;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const idx = (y * size + x) * 4;
        if (dist <= radius) {
          const angle = Math.atan2(dy, dx);
          const hue = ((angle * 180) / Math.PI + 360) % 360;
          const sat = Math.min(1, dist / radius);
          const [r, g, b] = hslToRgb(hue / 360, sat, light);
          img.data[idx] = r;
          img.data[idx + 1] = g;
          img.data[idx + 2] = b;
          img.data[idx + 3] = 255;
        } else {
          img.data[idx + 3] = 0;
        }
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [size, light]);

  const pickAt = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const radius = size / 2;
    const dx = x - radius;
    const dy = y - radius;
    let dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > radius) dist = radius;
    const angle = Math.atan2(dy, dx);
    const hue = ((angle * 180) / Math.PI + 360) % 360;
    const sat = Math.min(1, dist / radius);
    const [r, g, b] = hslToRgb(hue / 360, sat, light);
    onPick(rgbToHex(r, g, b));
  };

  const radius = size / 2;
  const angle = h * 2 * Math.PI;
  const dotX = radius + Math.cos(angle) * s * radius;
  const dotY = radius + Math.sin(angle) * s * radius;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          onMouseDown={(e) => { setDragging(true); pickAt(e.clientX, e.clientY); }}
          onMouseMove={(e) => dragging && pickAt(e.clientX, e.clientY)}
          onMouseUp={() => setDragging(false)}
          onMouseLeave={() => setDragging(false)}
          style={{ width: size, height: size, cursor: 'crosshair', borderRadius: '50%' }}
        />
        <div
          className="absolute pointer-events-none rounded-full border-2 border-white shadow-md"
          style={{
            width: 12,
            height: 12,
            left: dotX - 6,
            top: dotY - 6,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
          }}
        />
      </div>
      <div className="w-full px-1">
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(light * 100)}
          onChange={(e) => {
            const l = Number(e.target.value) / 100;
            setLight(l);
            const [r, g, b] = hslToRgb(h, s, l);
            onPick(rgbToHex(r, g, b));
          }}
          className="w-full h-2 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #000, ${rgbToHex(...hslToRgb(h, s, 0.5))}, #fff)`,
          }}
        />
      </div>
    </div>
  );
}

function ColorPicker({ value, onPick }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(value || '#579bfc');
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => { setCustom(value || '#579bfc'); }, [value]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const width = 248;
    const height = 320;
    const margin = 8;
    let left = rect.left;
    let top = rect.bottom + 4;
    if (left + width + margin > window.innerWidth) left = window.innerWidth - width - margin;
    if (left < margin) left = margin;
    if (top + height + margin > window.innerHeight) top = rect.top - height - 4;
    if (top < margin) top = margin;
    setPos({ left, top });
  }, [open]);

  const applyCustom = (hex) => {
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      onPick(hex);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="w-7 h-7 rounded-md border-2 border-white/20 shrink-0 transition-transform hover:scale-110"
        style={{ backgroundColor: value }}
        title="Escolher cor"
      />
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[71] bg-card border border-border rounded-xl p-4 shadow-2xl w-[220px]"
            style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}
          >
            <ColorWheel value={value} onPick={(c) => { setCustom(c); onPick(c); }} size={180} />
            <div className="mt-3 flex items-center gap-1.5">
              <input
                type="color"
                value={custom}
                onChange={(e) => { setCustom(e.target.value); onPick(e.target.value); }}
                className="w-8 h-7 rounded cursor-pointer border-0 p-0 bg-transparent shrink-0"
              />
              <input
                type="text"
                value={custom}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustom(v);
                  if (/^#[0-9a-fA-F]{6}$/.test(v)) onPick(v);
                }}
                onKeyDown={(e) => e.key === 'Enter' && applyCustom(custom)}
                maxLength={7}
                placeholder="#rrggbb"
                className="flex-1 min-w-0 bg-main border border-border/50 rounded px-2 py-1 text-xs text-text-primary outline-none focus:border-accent/50 font-mono"
              />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

const PHASE_SHORT_LABEL = {
  '': 'Sem fase',
  unstarted: 'Não iniciado',
  started: 'Em andamento',
  waiting: 'Esperando',
  completed: 'Concluído',
  canceled: 'Cancelado',
};

export default function EditStatusOptionsModal({ column, options, onClose }) {
  const {
    createStatusOption, updateStatusOption, deleteStatusOption,
    setColumnWorkflowRole, setColumnAssigneeColumn,
  } = useBoardStore();
  const columns = useBoardStore((s) => s.columns);
  const showError = useToastStore((s) => s.showError);
  // M3c: mesma capability que já governa etiquetas (board.status.manage) —
  // CAN_RULES.edit_structure (boardRules.js) só é true quando o backend
  // também libera status.manage, então este é o gate certo sem precisar de
  // uma chave nova em my_access.can.
  const canManageStatus = useBoardCan().edit_structure;
  const [saving, setSaving] = useState(null);
  const [primaryBusy, setPrimaryBusy] = useState(false);
  const [replacementFor, setReplacementFor] = useState(null);
  const [replacementId, setReplacementId] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  // A prop `column` reflete o momento em que o dropdown foi aberto; lê a
  // versão viva do store para workflow_role/assignee_column_id, que mudam
  // dentro da própria sessão deste modal (toggle de primary, select de pessoa).
  const liveColumn = columns.find((c) => c.id === column.id) || column;
  const isPrimary = liveColumn.workflow_role === 'primary';
  const otherPrimaryColumn = columns.find((c) => c.workflow_role === 'primary' && c.id !== column.id);
  const personColumns = columns.filter((c) => c.type === 'person');
  const missingPhaseCount = options.filter((o) => !o.phase).length;
  const replacementOptions = options.filter((o) => o.id !== replacementFor?.id);

  const handleLabelChange = (opt, label) => {
    if (label === opt.label) return;
    setSaving(opt.id);
    updateStatusOption(opt.id, column.id, { label }).finally(() => setSaving(null));
  };

  const handleColorChange = (opt, color) => {
    setSaving(opt.id);
    updateStatusOption(opt.id, column.id, { color }).finally(() => setSaving(null));
  };

  const handlePhaseChange = (opt, phase) => {
    setSaving(opt.id);
    updateStatusOption(opt.id, column.id, { phase })
      .catch((err) => showError(err.message || 'Não foi possível salvar a fase da etiqueta'))
      .finally(() => setSaving(null));
  };

  const handleTogglePrimary = async (e) => {
    const checked = e.target.checked;
    setPrimaryBusy(true);
    try {
      if (!checked) {
        await setColumnWorkflowRole(column.id, null);
        return;
      }
      await setColumnWorkflowRole(column.id, 'primary');
    } catch (err) {
      if (checked && err.status === 409) {
        const otherName = otherPrimaryColumn?.name || 'outra coluna';
        const confirmed = await dialog.confirm(
          'Trocar a coluna principal de status?',
          `O quadro já usa "${otherName}" como coluna principal — ela alimenta as métricas de fase hoje. Tornar "${liveColumn.name}" a nova principal? "${otherName}" deixa de alimentar as métricas.`,
          { confirmLabel: 'Trocar', danger: false }
        );
        if (confirmed) {
          try {
            if (otherPrimaryColumn) await setColumnWorkflowRole(otherPrimaryColumn.id, null);
            await setColumnWorkflowRole(column.id, 'primary');
          } catch (err2) {
            showError(err2.message || 'Não foi possível trocar a coluna principal de status');
          }
        }
      } else {
        showError(err.message || 'Não foi possível atualizar a coluna principal de status');
      }
    } finally {
      setPrimaryBusy(false);
    }
  };

  const handleAssigneeChange = async (e) => {
    const raw = e.target.value;
    try {
      await setColumnAssigneeColumn(column.id, raw === '' ? null : Number(raw));
    } catch (err) {
      showError(err.message || 'Não foi possível vincular a coluna de pessoa');
    }
  };

  // M3c, item 2: primeira tentativa nunca manda replacementOptionId — se o
  // backend recusar com 400 é porque a etiqueta está em uso (única razão de
  // 400 neste caminho, ver statusOption.controller.js), e só então abre o
  // pedido de substituta em vez de propagar o erro cru.
  const attemptDelete = async (opt, replacementOptionId) => {
    setDeleteBusy(true);
    try {
      await deleteStatusOption(opt.id, column.id, replacementOptionId ?? undefined);
      setReplacementFor(null);
      setReplacementId('');
    } catch (err) {
      if (err.status === 400 && replacementOptionId == null) {
        setReplacementFor(opt);
        setReplacementId('');
      } else {
        showError(err.message || 'Não foi possível excluir a etiqueta');
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleDelete = async (opt) => {
    if (!(await dialog.confirm(`Remover etiqueta "${opt.label}"`, 'A etiqueta será removida desta coluna.', { confirmLabel: 'Remover', danger: true }))) return;
    attemptDelete(opt, null);
  };

  const confirmReplacement = () => {
    if (!replacementId || !replacementFor) return;
    attemptDelete(replacementFor, Number(replacementId));
  };

  const handleAdd = () => {
    const hue = (options.length * 47) % 360;
    const [r, g, b] = hslToRgb(hue / 360, 0.65, 0.55);
    createStatusOption(column.id, 'Nova etiqueta', rgbToHex(r, g, b));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[440px] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <h3 className="text-sm font-semibold text-text-primary">
            Editar etiquetas — {column.name}
          </h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        {canManageStatus && (
          <div className="px-5 py-4 border-b border-border/50 space-y-3">
            <div>
              <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPrimary}
                  disabled={primaryBusy}
                  onChange={handleTogglePrimary}
                  className="w-3.5 h-3.5 rounded border-border bg-transparent accent-accent"
                />
                Coluna principal de status deste quadro
              </label>
              <p className="text-[11px] text-text-secondary mt-1 pl-5">
                A coluna principal alimenta as métricas de fase (quem iniciou, quem concluiu, quando).
                Só uma coluna de status por quadro pode ser a principal.
              </p>
            </div>

            {isPrimary && (
              <div className="pl-5">
                <label className="block text-[11px] text-text-secondary mb-1">
                  Coluna de pessoa vinculada
                </label>
                <select
                  value={liveColumn.assignee_column_id ?? ''}
                  onChange={handleAssigneeChange}
                  className="w-full bg-main border border-border/50 rounded px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50"
                >
                  <option value="">Nenhuma</option>
                  {personColumns.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-text-secondary mt-1">
                  Usada para saber quem estava com o item quando a fase mudou (ex.: "fulano iniciou às 14h").
                </p>
              </div>
            )}
          </div>
        )}

        <div className="p-4 overflow-y-auto flex-1">
          {missingPhaseCount > 0 && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                {missingPhaseCount} {missingPhaseCount === 1 ? 'etiqueta sem fase' : 'etiquetas sem fase'} —
                {missingPhaseCount === 1 ? ' fica' : ' ficam'} fora das métricas de status até alguém escolher a fase.
              </span>
            </div>
          )}

          <div className="space-y-2">
            {options.map((opt) => (
              <div
                key={opt.id}
                className={`flex items-center gap-2 rounded-lg px-1 py-1 ${!opt.phase ? 'ring-1 ring-amber-500/40 bg-amber-500/5' : ''}`}
              >
                <GripVertical className="w-4 h-4 text-text-secondary/40 shrink-0" />
                <ColorPicker value={opt.color} onPick={(c) => handleColorChange(opt, c)} />
                <div
                  className="w-2 h-7 rounded-sm shrink-0"
                  style={{ backgroundColor: opt.color }}
                />
                <input
                  defaultValue={opt.label}
                  onBlur={(e) => handleLabelChange(opt, e.target.value.trim() || opt.label)}
                  onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                  className="flex-1 min-w-0 bg-main border border-border/50 rounded px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50"
                />
                {canManageStatus ? (
                  <select
                    value={opt.phase ?? ''}
                    onChange={(e) => handlePhaseChange(opt, e.target.value || null)}
                    title="Fase no fluxo de status"
                    className="w-[136px] shrink-0 bg-main border border-border/50 rounded px-1.5 py-1.5 text-[11px] text-text-primary outline-none focus:border-accent/50"
                  >
                    {PHASE_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className="w-[90px] shrink-0 text-[11px] text-text-secondary text-right truncate">
                    {PHASE_SHORT_LABEL[opt.phase ?? '']}
                  </span>
                )}
                {saving === opt.id && (
                  <span className="text-[10px] text-text-secondary shrink-0">...</span>
                )}
                <button
                  onClick={() => handleDelete(opt)}
                  className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                  title="Remover"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {options.length === 0 && (
              <div className="text-center py-6 text-sm text-text-secondary">
                Nenhuma etiqueta ainda.
              </div>
            )}
          </div>

          <button
            onClick={handleAdd}
            className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-accent border border-dashed border-accent/40 rounded-lg hover:bg-accent/5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nova etiqueta
          </button>
        </div>

        <div className="px-5 py-3 border-t border-border/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-primary bg-accent hover:bg-accent/80 rounded-lg transition-colors"
          >
            Concluído
          </button>
        </div>
      </div>

      {replacementFor && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60"
          onClick={(e) => { e.stopPropagation(); setReplacementFor(null); }}
        >
          <div
            className="bg-card rounded-xl border border-border shadow-2xl w-[360px] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-sm font-semibold text-text-primary mb-2">Etiqueta em uso</h4>
            <p className="text-xs text-text-secondary mb-3">
              Cartões estão usando a etiqueta "{replacementFor.label}". Escolha para qual etiqueta eles serão
              movidos antes da exclusão.
            </p>
            {replacementOptions.length === 0 ? (
              <p className="text-xs text-danger mb-4">
                Não há outra etiqueta nesta coluna para receber os cartões. Crie uma nova etiqueta antes de excluir esta.
              </p>
            ) : (
              <select
                value={replacementId}
                onChange={(e) => setReplacementId(e.target.value)}
                className="w-full bg-main border border-border/50 rounded px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50 mb-4"
              >
                <option value="">Selecione a etiqueta substituta...</option>
                {replacementOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setReplacementFor(null)}
                className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmReplacement}
                disabled={!replacementId || deleteBusy}
                className="px-4 py-2 text-sm font-medium bg-danger text-white rounded-lg hover:bg-danger/80 transition-colors disabled:opacity-40"
              >
                Mover e excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
